import React, { useEffect, useState, useMemo } from 'react';
import './completedvids.css';
import { useNavigate } from 'react-router-dom';
import Header from '../../Components/Header/Header';
import { useAuthContext } from '../../hooks/UseAuthContext';
import Loader from '../../Components/Loader/Loader';
import api from '../../api';

const VIDEOS_PER_PAGE = 12;

function CompletedVids() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();
  const { user } = useAuthContext();

  useEffect(() => {
    api.get('/users', { headers: { Authorization: `Bearer ${user.token}` } })
      .then(r => setUsers(r.data))
      .catch(e => console.error('Error fetching users', e));
  }, [user?.token]);

  useEffect(() => {
    api.get('/completed-videos', {
      headers: { Authorization: `Bearer ${user?.token}`, 'Content-Type': 'application/json' }
    })
      .then(r => { setVideos(r.data); setLoading(false); })
      .catch(e => { console.error('Error fetching videos:', e); setLoading(false); });
  }, [user?.token]);

  const filteredVideos = useMemo(() => {
    const filtered = selectedUserEmail
      ? videos.filter(v => v.user_id === selectedUserEmail) : videos;
    return filtered.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [videos, selectedUserEmail]);

  const totalPages = Math.ceil(filteredVideos.length / VIDEOS_PER_PAGE);
  const startIndex = (currentPage - 1) * VIDEOS_PER_PAGE;
  const currentVideos = filteredVideos.slice(startIndex, startIndex + VIDEOS_PER_PAGE);

  useEffect(() => { setCurrentPage(1); }, [selectedUserEmail]);

  const handleNavigate = (id, data) => navigate(`/completed/${id}`, { state: { videoUrl: data } });

  const handleSendToTimeline = (e, video) => {
    e.stopPropagation();
    navigate('/timeline', {
      state: {
        timelineVideo: {
          url: `${process.env.REACT_APP_API_URL_VIDS}/videos/${video?.video_path}`,
          filename: video?.video_path?.split('/').pop() || video?.title || 'video.mp4'
        }
      }
    });
  };

  const handlePageChange = (page) => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) { for (let i = 1; i <= totalPages; i++) pages.push(i); }
    else if (currentPage <= 3) { pages.push(1, 2, 3, 4, '...', totalPages); }
    else if (currentPage >= totalPages - 2) { pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages); }
    else { pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages); }
    return pages;
  };

  return (
    <div className="completed-vids-container">
      <Header />
      <div className="completed-vids">
        <div className="complete-vid-header">
          <h2>Completed Videos</h2>
          <h3>Filter By User</h3>
          <select className="select-user-video" value={selectedUserEmail}
            onChange={(e) => setSelectedUserEmail(e.target.value)}>
            <option value="">All Users</option>
            {users?.map((u, i) => <option key={i} value={u.email}>{u.firstName} {u.lastName}</option>)}
          </select>
        </div>

        {currentVideos.length > 0 ? (
          <>
            <div className="videos">
              {currentVideos.map((video) => (
                <div key={video._id} className="video-element" onClick={() => handleNavigate(video._id, video)}>
                  <video src={`${process.env.REACT_APP_API_URL_VIDS}/videos/${video?.video_path}`} preload="metadata" />
                  <h3>{video.title || 'Untitled Video'}</h3>
                  <button className="send-to-timeline-btn" onClick={(e) => handleSendToTimeline(e, video)} title="Open in Timeline Editor">
                    ✂ Edit in Timeline
                  </button>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <div className="pagination-info">
                  Showing {startIndex + 1}-{Math.min(startIndex + VIDEOS_PER_PAGE, filteredVideos.length)} of {filteredVideos.length}
                </div>
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Prev</button>
                {getPageNumbers().map((page, i) =>
                  page === '...' ? <span key={`d${i}`} className="page-dots">...</span> :
                  <button key={page} onClick={() => handlePageChange(page)}
                    className={currentPage === page ? 'active' : ''}>{page}</button>
                )}
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state"><p>No videos found.</p></div>
        )}
      </div>
      {loading && <Loader />}
    </div>
  );
}

export default CompletedVids;